/**
 * When to re-roll a one-to-one stream that is not carrying media.
 *
 * A guest's one-to-one stream is joined by the guest (publisher) and the
 * host (receiver). Its stream part forms like any first join, ~2 s in a
 * two-node network with the entry-point fetch sometimes taking 4–8 s, and
 * once in a while it forms but carries nothing. Leaving and rejoining
 * re-rolls the topology, but a rejoin that lands inside the other side's
 * forming handshake tears it down (live session 2026-10-16: the guest's
 * blind 2 s timer fired before the part had formed, the host's 5 s timer
 * followed, and the two chains thrashed for 7 s). So a rejoin is only
 * decided from the state of the part: not formed for a long time, or
 * formed with no media for a while. Pure: no timers, no network.
 *
 * The guest owns the re-roll (its join direction is the one that forms
 * reliably); the host runs the same policy with longer waits as a fallback,
 * so the two sides never act on the same silence at once.
 */
/**
 * The guest acts first; the host only when the guest's re-rolls did not
 * help. A first join forms a brand-new part (1–4 s on the harness, the
 * live session 2.3 s) and the host's mix follows the guest's audio, so the
 * guest waits that out; a join still without a neighbor after 5 s is stuck
 * (the network layer's own retries at 1, 3 and 7 s did not help one that
 * was) and a re-roll forms it within a few hundred milliseconds.
 */
export const GUEST_ONE_TO_ONE_REJOIN = {
    unformedMs: 5000,
    formedNoMediaMs: 4000,
    maxRejoins: 3,
};
/**
 * A guest rejoining a part the host already sits on (a reload restoring its
 * role): formation takes 0.2–0.5 s when it works and otherwise stays stuck,
 * so the re-roll comes early — the old 2 s cadence, now only when nothing
 * arrives.
 */
export const GUEST_EXISTING_PART_REJOIN = {
    unformedMs: 2000,
    formedNoMediaMs: 2000,
    maxRejoins: 3,
};
export const HOST_ONE_TO_ONE_REJOIN = {
    unformedMs: 10_000,
    formedNoMediaMs: 8000,
    maxRejoins: 3,
};
export class OneToOneRejoinPolicy {
    t;
    joinedAtMs;
    formedAtMs;
    rejoins = 0;
    constructor(t, joinedAtMs) {
        this.t = t;
        this.joinedAtMs = joinedAtMs;
    }
    /** called after this side left and joined the part again */
    rejoined(nowMs) {
        this.rejoins += 1;
        this.joinedAtMs = nowMs;
        this.formedAtMs = undefined;
    }
    getRejoinCount() {
        return this.rejoins;
    }
    evaluate(inputs) {
        if (inputs.mediaFlowing) {
            return { type: 'done' };
        }
        if (inputs.neighborCount > 0) {
            this.formedAtMs ??= inputs.nowMs;
            const waitedMs = inputs.nowMs - this.formedAtMs;
            if (waitedMs < this.t.formedNoMediaMs) {
                return { type: 'wait', formed: true };
            }
            return this.rejoinOrGiveUp('formed-no-media', waitedMs);
        }
        // a neighbor that went away: the part is forming again
        this.formedAtMs = undefined;
        const waitedMs = inputs.nowMs - this.joinedAtMs;
        if (waitedMs < this.t.unformedMs) {
            return { type: 'wait', formed: false };
        }
        return this.rejoinOrGiveUp('not-formed', waitedMs);
    }
    rejoinOrGiveUp(reason, waitedMs) {
        if (this.rejoins >= this.t.maxRejoins) {
            return { type: 'give-up' };
        }
        return { type: 'rejoin', reason, attempt: this.rejoins + 1, waitedMs };
    }
}
